import 'cordova-plugin-purchase';
import * as Sentry from '@sentry/react';
import { isApprovedWatchDutyTransaction } from './detectIapCollision';

/**
 * On iOS `restorePurchases()` resolves after StoreKit reports the restore
 * complete and receipts refresh, so replays are usually already in hand. The
 * poll is margin for that not holding; kept short because a clean Apple ID has
 * nothing to short-circuit on and burns the whole ceiling behind a loader.
 */
const RESTORE_TIMEOUT_MS = 1_500;
const POLL_INTERVAL_MS = 150;

const findApproved = (): CdvPurchase.Transaction[] =>
  CdvPurchase.store.localTransactions.filter(isApprovedWatchDutyTransaction);

const pollForApproved = (): Promise<CdvPurchase.Transaction[]> =>
  new Promise<CdvPurchase.Transaction[]>((resolve) => {
    const startedAt = Date.now();

    const intervalId = setInterval(() => {
      const approved = findApproved();
      const expired = Date.now() - startedAt >= RESTORE_TIMEOUT_MS;

      if (approved.length || expired) {
        clearInterval(intervalId);
        resolve(approved);
      }
    }, POLL_INTERVAL_MS);
  });

/**
 * Forces StoreKit to replay this Apple ID's transactions into
 * `localTransactions`, then returns our approved (unfinished) ones. A
 * correctly finished purchase leaves an empty queue, so without this an Apple
 * ID that already owns a Watch Duty subscription looks brand new.
 *
 * Returns facts, not a verdict: collision vs. the caller's own re-purchase
 * depends on the signed-in account, which this does not know about.
 *
 * Fails open, so "nothing found" is ambiguous and a failed restore is always
 * reported.
 */
export const restoreAndFindApprovedTransactions = async (): Promise<
  CdvPurchase.Transaction[]
> => {
  const startedAt = Date.now();
  let restoreError: CdvPurchase.IError | Error | null = null;

  try {
    // Resolves with an `IError` instead of throwing, so both channels matter:
    // a swallowed failure looks like a clean Apple ID.
    restoreError = (await CdvPurchase.store.restorePurchases()) ?? null;
  } catch (err) {
    restoreError = err instanceof Error ? err : new Error(String(err));
  }

  // Nothing is coming after a failed restore, so skip the wait.
  const approved = restoreError ? findApproved() : await pollForApproved();

  const durationMs = Date.now() - startedAt;
  const transactionCount = CdvPurchase.store.localTransactions.length;

  if (restoreError) {
    Sentry.captureMessage('iap-restore-errored', {
      level: 'info',
      extra: {
        durationMs,
        transactionCount,
        message: restoreError.message,
      },
    });
  } else if (!transactionCount) {
    // A clean Apple ID still replays the virtual `appstore.application`
    // transaction, so an empty queue means the bridge never answered.
    Sentry.captureMessage('iap-restore-timed-out', {
      level: 'info',
      extra: { durationMs },
    });
  }

  return approved;
};
